import * as React from 'react';
import { NewbuildingCarousel, TDirection, TOnSlideChangeParams } from '@cian/frontend-newbuilding-carousel-component';
import { equals } from 'ramda';
import { IBanner } from '../../types/banners';
import { Slide } from './parts/Slide';
import { ArrowControls } from './parts/ArrowControls';
import { firstPaintOptions, mediaSettings } from './config';
import * as styles from './Carousel.css';
import { NoImage } from '../NoImage';

interface ICarouselProps {
  banners: IBanner[];
  testId?: string;
}

interface ICarouselState {
  slides: React.ReactNode[];
  currentSlide: number;
  isShown: boolean;
}

/**
 * В NewbuildingCarousel при изменении слайдов происходит глубокая проверка
 * старых и новых слайдов. Из-за функции isDeepEqual из '@cian/newbuilding-utils',
 * а именно из-за JSON.stringify падает ошибка. В классовом компоненте все работает.
 */
export class Carousel extends React.Component<ICarouselProps, ICarouselState> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private intervalId: any;

  public constructor(props: ICarouselProps) {
    super(props);

    this.state = {
      slides: this.prepareSlides(this.props.banners),
      currentSlide: 1,
      isShown: false,
    };
  }

  public componentDidMount() {
    this.setState({ slides: this.prepareSlides(this.props.banners) });

    this.setSlidesInterval();
  }

  public componentDidUpdate(prevProps: Readonly<ICarouselProps>) {
    const { banners } = this.props;

    if (!equals(banners, prevProps.banners)) {
      this.setState({ slides: this.prepareSlides(this.props.banners), currentSlide: 1 });
    }
  }

  public componentWillUnmount() {
    clearInterval(this.intervalId);
  }

  private autoSlidesChange = () => {
    if (this.state.isShown) {
      return;
    }

    const { currentSlide, slides } = this.state;
    const nextSlide = slides.length === currentSlide ? 1 : currentSlide + 1;

    this.setState({ currentSlide: nextSlide });
  };

  private setIsShown = (value: boolean) => {
    this.setState({ isShown: value });
  };

  private setSlidesInterval = () => {
    this.intervalId = setInterval(this.autoSlidesChange, 4000);
  };

  private prepareSlides = (banners: IBanner[]) => {
    return banners.map((banner, index) => <Slide key={banner.id || index} banner={banner} />);
  };

  private handleSlideChange = ({ currentSlide }: TOnSlideChangeParams) => {
    clearInterval(this.intervalId);
    this.setState(
      {
        currentSlide,
      },
      () => {
        this.setSlidesInterval();
      },
    );
  };

  private handleArrowsClick = (direction: TDirection) => {
    this.setState({
      currentSlide: this.state.currentSlide + direction * 1,
    });
  };

  public render() {
    const { testId } = this.props;
    const { slides, currentSlide } = this.state;

    if (!slides.length) {
      return (
        <div className={styles['carousel']}>
          <NoImage />
        </div>
      );
    }

    return (
      <section
        className={styles['carousel']}
        data-testid={testId}
        onMouseEnter={() => this.setIsShown(true)}
        onMouseLeave={() => this.setIsShown(false)}
      >
        <NewbuildingCarousel
          media={mediaSettings}
          firstPaintOptions={firstPaintOptions}
          slides={slides}
          defaultCurrentSlide={currentSlide}
          onSlideChange={this.handleSlideChange}
        />

        {slides.length > 1 && (
          <ArrowControls
            disableLeft={currentSlide === 1}
            disableRight={currentSlide === slides.length}
            clickHandler={this.handleArrowsClick}
          />
        )}
      </section>
    );
  }
}
